import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  TrendingUp, DollarSign, PieChart, Calendar, 
  Trash2, Loader2, BarChart3, AlertTriangle
} from 'lucide-react';
import ExportButton from './ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesAnalytics = ({ token }) => {
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
    fetchSummary();
  }, [period, selectedEvent, selectedUser]);

  const fetchFilterOptions = async () => {
    try {
      const [eventsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/events`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/analytics/sales?period=${period}`;
      if (selectedEvent) url += `&event_id=${selectedEvent}`;
      if (selectedUser) url += `&user_id=${selectedUser}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      toast.error('Erreur lors du chargement des données de vente');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      let url = `${API}/admin/analytics/summary`;
      const params = [];
      if (selectedEvent) params.push(`event_id=${selectedEvent}`);
      if (selectedUser) params.push(`user_id=${selectedUser}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const clearFilters = () => {
    setSelectedEvent('');
    setSelectedUser('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  const hasFilters = selectedEvent || selectedUser;

  return (
    <div className="space-y-6">
      {/* Quick Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.today.commission)}</p>
                  <p className="text-xs text-green-600">{summary.today.count} transactions</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Cette semaine</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(summary.this_week.commission)}</p>
                  <p className="text-xs text-blue-600">{summary.this_week.count} transactions</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Ce mois</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrency(summary.this_month.commission)}</p>
                  <p className="text-xs text-purple-600">{summary.this_month.count} transactions</p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Cette année</p>
                  <p className="text-2xl font-bold text-amber-700">{formatCurrency(summary.this_year.commission)}</p>
                  <p className="text-xs text-amber-600">{summary.this_year.count} transactions</p>
                </div>
                <PieChart className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters Section */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filtres:</span>
              </div>
              
              <select
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm min-w-[200px]"
              >
                <option value="">Tous les événements</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name} {event.date ? `(${event.date.split('T')[0]})` : ''}
                  </option>
                ))}
              </select>
              
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm min-w-[200px]"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.email}
                  </option>
                ))}
              </select>
              
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Effacer filtres
                </Button>
              )}
            </div>
            
            {hasFilters && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                <AlertTriangle className="h-4 w-4" />
                Filtres actifs
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Period Selector */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-brand-600" />
              Analyse des ventes
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { value: 'daily', label: 'Jour' },
                { value: 'weekly', label: 'Semaine' },
                { value: 'monthly', label: 'Mois' },
                { value: 'quarterly', label: 'Trimestre' },
                { value: 'biannual', label: 'Semestre' },
                { value: 'annual', label: 'Année' }
              ].map(p => (
                <Button
                  key={p.value}
                  size="sm"
                  variant={period === p.value ? 'default' : 'outline'}
                  onClick={() => setPeriod(p.value)}
                  className={period === p.value ? 'bg-brand-600' : ''}
                >
                  {p.label}
                </Button>
              ))}
              <ExportButton token={token} exportType="sales" period={period} eventId={selectedEvent} userId={selectedUser} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : data && data.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Période</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Transactions</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Volume</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{row.period}</td>
                      <td className="py-3 px-4 text-right">{row.transactions}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(row.volume)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-green-600">{formatCurrency(row.commission)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr className="font-bold">
                    <td className="py-3 px-4">Total</td>
                    <td className="py-3 px-4 text-right">{data.totals.transactions}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(data.totals.volume)}</td>
                    <td className="py-3 px-4 text-right text-green-600">{formatCurrency(data.totals.commission)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesAnalytics;
